import React, { useEffect, useRef, useState } from 'react';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { ApplicationState } from 'store/rootReducer';
import { useDispatch } from 'react-redux';
import i18n from 'i18n';
import _ from 'lodash';
import {
  changeInspectionViewActivePage,
  changeInspectionViewPageSize,
} from 'store/analytics/ui/subViews/inspectionView/inspectionViewActions';
import { ArgumentPair, MessagingInspectionViewRequest, TxiViewRequest } from 'types/rest/analyticsTypes';
import { fetchInspectionViewRequestDetailThunk } from 'store/analytics/data/analyticsDataActions';
import { formatDateTimeFromMillis, formatDateTimeFromSeconds } from 'utils/dateUtil';
import MixpanelService from 'utils/mixpanelService';
import { SEARCH_FILTER_RESULTS } from 'utils/mixpanelEvents';
import { _findInArray, getTableOptions } from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewMar';
import { InspectionDataState } from 'store/analytics/data/analyticsDataTypes';
import TransactionInspectionDetails from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/TransactionInspectionDetails';
import styles from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewTransactional.module.css';
import clsx from 'clsx';
import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import CopyToClipboardButton from 'components/buttons/CopyToClipboardButton';
import { getColoredHttpMethod, getPath } from 'utils/commonUtil';
import { Transaction } from '../../../../types/rest/assetCatalogTypes';
import { getTransaction } from '../../../../store/transactions/transactionsActions';
import { showToastNotification } from '../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../store/ui/toasts/toastsTypes';
import { getErrorMessage } from '../../../../utils/miscUtil';
import { useIsMount } from '../../../../hooks/useIsMountHook';
import store from '../../../../store/configureStore';

interface MessagingRunnerProps {
  setTransaction: (transaction: Transaction | null) => void;
}

interface FilteredResults {
  requests: any[];
  tableOptions: BlazeTableOptions;
  sliceData: any;
}

const MessagingRunnerView = (props: MessagingRunnerProps) => {
  const isMount = useIsMount();
  const dispatch = useDispatch();

  const state = store.getState() as ApplicationState;
  const activeUiContext = state.analytics.ui.activeUiContext;
  const activeDataContext = state.analytics.data.activeDataContext;

  const tableRef = useRef<any>(null);

  const generateCondensedTableDom = (elements: string[]) => {
    return (
      <table className="table table-condensed table-striped mb-0">
        {elements.map((item, index) => (
          <tr key={item}>
            <td style={{ borderTop: index === 0 ? 'none' : undefined, paddingLeft: 0 }}>
              <span title={item} className="text-truncate">
                {item}
              </span>
            </td>
          </tr>
        ))}
      </table>
    );
  };

  const nestedTableCell = (txiViewArguments: ArgumentPair[] | null, accessor: string) => {
    if (_.isNil(txiViewArguments) || (txiViewArguments && txiViewArguments.length === 0)) {
      return null;
    }
    const listOfValues: string[] = txiViewArguments.map((item: any) => item[accessor]);
    if (listOfValues.length > 1) {
      return generateCondensedTableDom(listOfValues);
    } else {
      return listOfValues[0];
    }
  };

  const openTxnDetailHandler = async (txnId: string, e: any) => {
    //Do not load analytics detail
    e.stopPropagation();
    //Get txn
    try {
      const txn = (await getTransaction(txnId)).data.result;
      props.setTransaction(txn);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve transaction', 'Reason: ' + errorMsg));
    }
  };

  const colsDefinition: BlazeTableColDef[] = [
    {
      header: i18n.t('analytics:txInspection.table.timestamp'),
      accessor: 'timestamp',
      width: 140,
      cellComponent: (rowData: MessagingInspectionViewRequest) => {
        if (_.isNil(rowData.timestamp)) {
          return 'N/A';
        }
        const dateTimeWithMillis = formatDateTimeFromMillis(parseInt(rowData.timestamp));
        return (
          <span title={dateTimeWithMillis}>{formatDateTimeFromSeconds(parseInt(rowData.timestamp) / 1000, true)}</span>
        );
      },
    },
    {
      header: i18n.t('analytics:txInspection.table.messaging.messageType'),
      width: 140,
      accessor: 'messageType',
    },
    {
      header: i18n.t('analytics:txInspection.table.messaging.txnId'),
      cellComponent: (request: TxiViewRequest) => {
        if (!_.isNil(request.matched) && request.matched === true) {
          return (
            <button
              onClick={e => openTxnDetailHandler(request.transactionId, e)}
              className={'btn btn-link no-hover'}
              style={{ padding: 0 }}
            >
              {request.transactionId ? request.transactionId.toString() : null}
            </button>
          );
        } else {
          return 'no match';
        }
      },
      width: 120,
      accessor: 'transactionId',
    },
    {
      header: i18n.t('analytics:txInspection.table.messaging.originName'),
      width: 140,
      accessor: 'origin',
    },
    {
      header: i18n.t('analytics:txInspection.table.messaging.originType'),
      width: 100,
      accessor: 'originType',
    },
    {
      header: i18n.t('analytics:txInspection.table.messaging.destinationName'),
      cellComponent: (request: MessagingInspectionViewRequest) => {
        if (request.destinations) {
          return nestedTableCell(
            request.destinations.map(
              dest => ({ key: dest.destinationName, value: dest.destinationType } as ArgumentPair),
            ),
            'key',
          );
        } else {
          return <></>;
        }
      },
      accessor: 'destinationNames',
      width: 100,
    },
    {
      header: i18n.t('analytics:txInspection.table.messaging.destinationType'),
      cellComponent: (request: MessagingInspectionViewRequest) => {
        if (request.destinations) {
          return nestedTableCell(
            request.destinations.map(
              dest => ({ key: dest.destinationName, value: dest.destinationType } as ArgumentPair),
            ),
            'value',
          );
        } else {
          return <></>;
        }
      },
      accessor: 'destinationTypes',
      width: 100,
    },
    {
      header: i18n.t('analytics:txInspection.table.messaging.txnPriority'),
      width: 100,
      accessor: 'txnPriority',
    },
  ];

  const filterRequestList = (searchKeyword: string) => {
    const requests = getRequestsFromDataContext();

    if (!_.isEmpty(searchKeyword)) {
      return requests.filter((item: any) => {
        return (
          (item.messageType ? item.messageType.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.id
            ? item.id
                .toString()
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.transactionId
            ? item.transactionId
                .toString()
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.timestamp
            ? formatDateTimeFromSeconds(parseInt(item.timestamp) / 1000)
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.origin ? item.origin.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.originType ? item.originType.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.destinationNames ? item.destinationNames.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.destinationTypes ? item.destinationTypes.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.txnPriority
            ? item.txnPriority
                .toString()
                .toLocaleLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          _findInArray(item.arguments, searchKeyword)
        );
      });
    } else {
      return requests;
    }
  };

  const getRequestsFromDataContext = () => {
    if (_.isNil(activeDataContext)) {
      return [];
    }
    const txiDataState: InspectionDataState = activeDataContext.inspection;
    return (txiDataState.requests as MessagingInspectionViewRequest[]) || [];
  };

  //const activeUiContext = props.activeUiContext;
  const activePage = activeUiContext ? activeUiContext.inspectionView.activePage : 1;
  const pageSize = activeUiContext ? activeUiContext.inspectionView.pageSize : 5;
  const selectedRequest = activeUiContext ? activeUiContext.inspectionView.selectedRequest : null;
  const searchKeyword = activeUiContext ? activeUiContext.inspectionView.searchKeyword : '';

  // const activeDataContext = props.activeDataContext;
  const isRequestListLoading = activeDataContext ? activeDataContext.inspection.isLoadingRequests : false;
  const lastRefresh =
    activeDataContext !== null &&
    activeDataContext.inspection !== null &&
    activeDataContext.inspection.requestsLoadedTs !== null
      ? new Date(activeDataContext.inspection.requestsLoadedTs)
      : null;
  const tabId = activeDataContext ? activeDataContext.tabId : null;

  const requests = getRequestsFromDataContext();

  const tableOptions: BlazeTableOptions = {
    showRowExpander: false,
    columnDef: colsDefinition,
    pageable: true,
    sortable: true,
    selectable: false,
    pageSize: 5,
    minWidth: 950,
    minHeight: 210,
    tableName: 'requestList',
    noResultsText: i18n.t('analytics:inspection.noResultsText'),
    customHeaderClassName: 'flex-table-item-top',
    customTableClassName: 'flex-table-simple flex-table-condensed',
    onRowClick: (requestId: number) => dispatch(fetchInspectionViewRequestDetailThunk(requestId, tabId)),
    customRowClassName: (request: any) => {
      let rowClass = 'hand-cursor ';
      if (selectedRequest && request.id.toString() === selectedRequest.id.toString()) {
        rowClass += styles.customEventClass;
      }
      return rowClass;
    },
    maxContentHeight: '186px',
    paginationOverride: {
      pageSize: pageSize,
      total: requests.length,
      activePage: activePage,
    },
    hidePager: requests.length === 0,
  };

  const initResult = filterRequestList(searchKeyword);
  const filterResult: FilteredResults = getTableOptions(initResult, searchKeyword, tableOptions, pageSize);

  return (
    <>
      <BlazeTable
        isLoading={isRequestListLoading}
        options={filterResult.tableOptions}
        onPageSizeChange={pageSize => dispatch(changeInspectionViewPageSize(pageSize))}
        onPageChange={pageNumber => dispatch(changeInspectionViewActivePage(pageNumber))}
        data={filterResult.sliceData}
        onRef={(ref: any) => (tableRef.current = ref)}
        showPageSizeDropdown={true}
      />
      {!_.isNil(selectedRequest) && !_.isNil(lastRefresh) && <TransactionInspectionDetails />}
    </>
  );
};

export default MessagingRunnerView;
